import { FC } from "react";

import { FormType } from "@simplyk/common";
import { DateTime } from "luxon";

import { useSalesOpenSchedule } from "./useSalesOpenSchedule";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormChip, FormChipProps } from "@/forms-design-system/FormChip";

interface SalesOpeningChipProps extends Omit<FormChipProps, "vibe" | "variant" | "label" | "data-test"> {
  isAuction?: boolean;
}

export const SalesOpeningChip: FC<SalesOpeningChipProps> = ({ isAuction = false, sx, ...rest }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isPhoneScreen } = useMediaQuery();
  const { formObject: ticketing } = useFormV2Context(FormType.Ticketing);
  const { salesOpenDate, isSalesScheduled } = useSalesOpenSchedule();

  if (!isSalesScheduled || !salesOpenDate) {
    return null;
  }

  const zoned = DateTime.fromJSDate(salesOpenDate)
    .setZone(ticketing?.eventTimezone ?? undefined)
    .setLocale(locale);
  const date = zoned.toLocaleString(DateTime.DATE_MED);
  const time = zoned.toLocaleString(DateTime.TIME_SIMPLE);

  const label = isAuction
    ? isPhoneScreen
      ? t("ticketing", "bidsOpenShort", { date, time })
      : t("ticketing", "bidsOpeningOn", { date, time })
    : t("ticketing", "salesOpenOn", { date, time });

  return (
    <FormChip
      vibe="positive"
      variant="outlined"
      label={label}
      data-test="sales-opening-chip"
      sx={[{ alignSelf: "flex-start" }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...rest}
    />
  );
};
